import React from "react";
import {
	Document,
	Font,
	Image,
	Page,
	StyleSheet,
	Text,
	View,
} from "@react-pdf/renderer";
import { DisaggregatedMetric, MetricMetadata } from "../services/types";
import {
	categorizeData,
	fetchPercentileById,
	getIndexMetricDataPDF,
	getPDFMetricIcon,
	getSortedMetrics,
	indexMetricIds,
} from "../utils/dashboardUtils";
import { categorizeIndexValue, formatValue, formatValueBase } from "../utils/helpers";
import laceyImg from "../assets/lacey-logo-horizontal-new.png";
import equityPng from "../assets/pngs/Equity.png";
import accessibilityPng from "../assets/pngs/Accessibility.png";
import livabilityPng from "../assets/pngs/Livability.png";
import educationPng from "../assets/pngs/Education.png";
import economyPng from "../assets/pngs/Economy.png";
import environmentPng from "../assets/pngs/Environment.png";

Font.registerHyphenationCallback((word) => [word]);

export type LaceyMetricCategory =
	| "Demographics"
	| "Accessibility"
	| "Livability"
	| "Education"
	| "Economy"
	| "Environment";

export type IndexMetricMap = Record<LaceyMetricCategory, DisaggregatedMetric | null>;

interface MetricsPDFProps {
	pdfImageBlob?: Blob;
	pdfUrl?: string;
	logoUrl?: string;
	legendUrl?: string;
	selectedArea: string;
	siteUrl?: string;
	disaggregatedData: DisaggregatedMetric[];
	indexMetrics: IndexMetricMap;
	indexCategories: Record<number, string>;
}

export interface MetricsPDFSourceProps extends Omit<
	MetricsPDFProps,
	"indexMetrics" | "indexCategories"
> {
	metadata: MetricMetadata[] | null;
}

export interface PreparedMetricsPDFIndexData {
	indexMetrics: IndexMetricMap;
	indexCategories: Record<number, string>;
}

const LACEY_BLUE = "#225591";
const CHART_GREEN = "#00A18A";
const SPECIAL_DEMOGRAPHIC_IDS = new Set([1134, 1135, 1182, 1147, 1148]);
const PDF_CATEGORIES: LaceyMetricCategory[] = [
	"Demographics",
	"Accessibility",
	"Livability",
	"Education",
	"Economy",
	"Environment",
];

const TITLE_BANNER_ICONS: Record<LaceyMetricCategory | "Equity", string> = {
	Demographics: equityPng,
	Equity: equityPng,
	Accessibility: accessibilityPng,
	Livability: livabilityPng,
	Education: educationPng,
	Economy: economyPng,
	Environment: environmentPng,
};

const styles = StyleSheet.create({
	page: {
		paddingTop: 24,
		paddingHorizontal: 24,
		paddingBottom: 34,
		fontFamily: "Helvetica",
		color: "#4D4D4D",
		backgroundColor: "#FFFFFF",
	},
	mapHeader: {
		minHeight: 82,
		backgroundColor: "#F1F8FA",
		borderWidth: 1,
		borderColor: "#C6DCE5",
		borderTopWidth: 5,
		borderTopColor: "#005596",
		borderBottomWidth: 3,
		borderBottomColor: "#00A18A",
		borderRadius: 6,
		paddingVertical: 10,
		paddingHorizontal: 16,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	laceyLogoContainer: {
		width: "62%",
		alignItems: "flex-start",
	},

	laceyLogo: {
		width: "100%",
		height: 55,
		objectFit: "contain",
	},

	mapHeaderCopy: {
		width: "34%",
		alignItems: "flex-end",
	},

	mapHeaderTitle: {
		color: "#005596",
		fontSize: 14,
		fontWeight: 700,
		textAlign: "right",
	},

	mapHeaderText: {
		color: "#3B5967",
		fontSize: 8.5,
		marginTop: 3,
		textAlign: "right",
	},

	mapHeaderUrl: {
		color: "#007F71",
		fontSize: 7.5,
		fontWeight: 700,
		marginTop: 4,
		textAlign: "right",
	},
	mapImage: {
		width: "100%",
		maxHeight: 560,
		objectFit: "contain",
		marginTop: 12,
	},

	legendOverlay: {
		width: 125,
		height: 120,

		// Pull the legend upward into the map.
		marginTop: -132,
		marginLeft: 12,

		// Restores the normal flow position for the content below.
		marginBottom: 12,

		padding: 7,
		backgroundColor: "#FFFFFF",
		borderWidth: 0.5,
		borderColor: "#A9B6BC",
		borderRadius: 5,
	},

	legendImage: {
		width: 111,
		height: 106,
		objectFit: "contain",
	},

	mapAssets: {
		flexDirection: "row",
		justifyContent: "flex-end",
		alignItems: "center",
		marginTop: 8,
	},
	esriLogo: {
		width: 95,
		maxHeight: 40,
		objectFit: "contain",
	},
	documentHeader: { flexDirection: "row", justifyContent: "space-between" },
	documentHeaderText: { color: "#4D4D4D", fontSize: 9, fontWeight: 700 },
	documentHeaderArea: { color: LACEY_BLUE, fontSize: 9, fontWeight: 700 },
	divider: { borderTopWidth: 1, borderTopColor: "#A9B6BC", marginTop: 4, marginBottom: 8 },
	titleBanner: {
		backgroundColor: LACEY_BLUE,
		borderRadius: 5,
		paddingVertical: 8,
		paddingHorizontal: 12,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	titleBannerHeading: { flexDirection: "row", alignItems: "center", flexShrink: 0 },
	titleBannerIcon: { width: 20, height: 20, objectFit: "contain", marginRight: 7 },
	titleBannerTitle: { color: "#FFFFFF", fontSize: 13, fontWeight: 700 },
	titleBannerIndex: { color: "#FFFFFF", fontSize: 8.5, marginLeft: 12 },
	titleBannerIndexStrong: { fontWeight: 700 },
	description: { color: "#4D4D4D", fontSize: 8, marginHorizontal: 8, marginTop: 9 },
	metricGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "flex-start",
		marginTop: 8,
		marginHorizontal: -6,
	},
	metricCard: {
		width: "48%",
		minHeight: 106,
		marginHorizontal: "1%",
		marginBottom: 8,
		borderWidth: 1,
		borderColor: "#DFE5E8",
		borderRadius: 9,
		backgroundColor: "#FFFFFF",
		overflow: "hidden",
	},
	metricCardIndex: {
		borderLeftWidth: 4,
		borderLeftColor: LACEY_BLUE,
	},
	metricCardContent: { padding: 10, flexGrow: 1 },
	metricTopRow: {
		flexDirection: "row",
		alignItems: "center",
		minHeight: 36,
	},
	metricIcon: {
		width: 38,
		height: 38,
		objectFit: "contain",
		marginRight: 10,
	},
	metricValue: { color: LACEY_BLUE, fontSize: 24, fontWeight: 700 },
	metricTitle: { color: "#343434", fontSize: 10, fontWeight: 700, marginTop: 4 },
	metricDescriptor: { color: "#343434", fontSize: 8, lineHeight: 1.2, marginTop: 2 },
	indexBadge: {
		alignSelf: "flex-start",
		backgroundColor: LACEY_BLUE,
		borderRadius: 8,
		color: "#FFFFFF",
		fontSize: 7,
		fontWeight: 700,
		marginTop: 2,
		paddingVertical: 2,
		paddingHorizontal: 6,
	},
	chartStack: { marginTop: 10 },
	chartSpacing: { marginBottom: 12 },
	footer: {
		position: "absolute",
		left: 24,
		right: 24,
		bottom: 15,
		flexDirection: "row",
		justifyContent: "space-between",
		borderTopWidth: 0.5,
		borderTopColor: "#D9E0E5",
		paddingTop: 5,
	},
	footerText: { color: "#7A858C", fontSize: 6.5 },
	empty: { color: "#65727C", fontSize: 9, marginTop: 18, textAlign: "center" },
});

// PDF chart components
// -----------------------------------------------------------------------------
// These charts intentionally use react-pdf View/Text primitives instead of
// Recharts. That keeps all labels and values crisp in the generated document
// and makes the information available without hover interactions.

interface PDFMetricBarChartProps {
	metric?: DisaggregatedMetric;
	barColor?: string;
	topFive?: boolean;
	title?: string;
	subtitle?: string;
}

const metricBarChartStyles = StyleSheet.create({
	card: {
		borderWidth: 1,
		borderColor: "#D9E0E5",
		borderRadius: 8,
		backgroundColor: "#FFFFFF",
		padding: 12,
	},
	title: { color: "#243746", fontSize: 11, fontWeight: 700 },
	subtitle: { color: "#65727C", fontSize: 8, marginTop: 2, marginBottom: 8 },
	empty: { color: "#65727C", fontSize: 9, paddingVertical: 18, textAlign: "center" },
	row: { flexDirection: "row", alignItems: "center", minHeight: 25, marginBottom: 5 },
	label: { width: 132, color: "#4D4D4D", fontSize: 8, paddingRight: 7 },
	track: { flexGrow: 1, height: 12, backgroundColor: "#EEF2F4", borderRadius: 3 },
	bar: { height: 12, borderRadius: 3, justifyContent: "center", paddingLeft: 4 },
	share: { color: "#FFFFFF", fontSize: 6.5, fontWeight: 700 },
	value: { width: 62, color: "#4D4D4D", fontSize: 7, paddingLeft: 6 },
});

const normalizeChartLabel = (value: string) =>
	value
		.replace(/\u00A0/g, " ")
		.replace(/\s+/g, " ")
		.replace(/\bNH\b/g, "Non-Hispanic")
		.trim();

const safeNumber = (value: unknown) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const PDFMetricBarChart: React.FC<PDFMetricBarChartProps> = ({
	metric,
	barColor = CHART_GREEN,
	topFive = false,
	title,
	subtitle,
}) => {
	const version = metric?.versions?.[0];
	const disaggregations = version?.disaggregations ?? [];
	const sorted = [...disaggregations].sort(
		(a, b) => safeNumber(b.value) - safeNumber(a.value),
	);
	const shown = topFive ? sorted.slice(0, 5) : sorted;
	const total = disaggregations.reduce((sum, item) => sum + safeNumber(item.value), 0);
	const max = Math.max(...shown.map((item) => safeNumber(item.value)), 0);

	return (
		<View style={metricBarChartStyles.card} wrap={false}>
			<Text style={metricBarChartStyles.title}>{title ?? metric?.name ?? "Metric"}</Text>
			{subtitle ? <Text style={metricBarChartStyles.subtitle}>{subtitle}</Text> : null}

			{shown.length === 0 ? (
				<Text style={metricBarChartStyles.empty}>No data available</Text>
			) : (
				<View style={{ marginTop: subtitle ? 2 : 8 }}>
					{shown.map((item) => {
						const value = safeNumber(item.value);
						const share = total > 0 ? value / total : 0;
						const width = max > 0 ? Math.max((value / max) * 100, 1) : 0;

						return (
							<View key={`${item.name}-${value}`} style={metricBarChartStyles.row}>
								<Text style={metricBarChartStyles.label}>{normalizeChartLabel(item.name)}</Text>
								<View style={metricBarChartStyles.track}>
									<View
										style={[
											metricBarChartStyles.bar,
											{ width: `${width}%`, backgroundColor: barColor },
										]}
									>
										{width >= 18 ? (
											<Text style={metricBarChartStyles.share}>
												{`${(share * 100).toFixed(0)}%`}
											</Text>
										) : null}
									</View>
								</View>
								<Text style={metricBarChartStyles.value}>
									{formatValue(value, version?.unit ?? "integer", metric?.id ?? 0)}
								</Text>
							</View>
						);
					})}
				</View>
			)}
		</View>
	);
};

interface PDFRaceEthnicityChartProps {
	raceMetric?: DisaggregatedMetric;
	ethnicityMetric?: DisaggregatedMetric;
	barColor?: string;
	title?: string;
}

interface RaceChartRow {
	name: string;
	value: number;
	share: number | null;
	section: "race" | "ethnicity";
}

const raceChartStyles = StyleSheet.create({
	card: {
		borderWidth: 1,
		borderColor: "#D9E0E5",
		borderRadius: 8,
		backgroundColor: "#FFFFFF",
		padding: 12,
	},
	title: { color: "#243746", fontSize: 11, fontWeight: 700, marginBottom: 8 },
	section: { color: "#65727C", fontSize: 7, fontWeight: 700, marginTop: 5, marginBottom: 3 },
	divider: { borderTopWidth: 1, borderTopColor: "#D9E0E5", marginTop: 6 },
	row: { flexDirection: "row", alignItems: "center", minHeight: 24, marginBottom: 4 },
	label: { width: 148, color: "#4D4D4D", fontSize: 7.5, paddingRight: 7 },
	track: { flexGrow: 1, height: 12, backgroundColor: "#EEF2F4", borderRadius: 3 },
	bar: { height: 12, borderRadius: 3, justifyContent: "center", paddingLeft: 4 },
	percentage: { color: "#FFFFFF", fontSize: 6.5, fontWeight: 700 },
	value: { width: 62, color: "#4D4D4D", fontSize: 7, paddingLeft: 6 },
	empty: { color: "#65727C", fontSize: 9, paddingVertical: 18, textAlign: "center" },
});

const getRaceRows = (metric?: DisaggregatedMetric): RaceChartRow[] =>
	[...(metric?.versions?.[0]?.disaggregations ?? [])]
		.sort((a, b) => safeNumber(b.value) - safeNumber(a.value))
		.map((item) => {
			const numerator = safeNumber(item.percent_calc?.n ?? item.value);
			const denominator = safeNumber(item.percent_calc?.d);

			return {
				name: normalizeChartLabel(item.name),
				value: numerator,
				share: denominator > 0 ? numerator / denominator : null,
				section: "race",
			};
		});

const getEthnicityRows = (metric?: DisaggregatedMetric): RaceChartRow[] => {
	const item = metric?.versions?.[0]?.disaggregations?.find(
		(candidate) => candidate.name === "Hispanic or Latino",
	);
	if (!item) return [];

	const numerator = safeNumber(item.percent_calc?.n ?? item.value);
	const denominator = safeNumber(item.percent_calc?.d);

	return [{
		name: "Hispanic or Latino",
		value: numerator,
		share: denominator > 0 ? numerator / denominator : null,
		section: "ethnicity",
	}];
};

const PDFRaceEthnicityChart: React.FC<PDFRaceEthnicityChartProps> = ({
	raceMetric,
	ethnicityMetric,
	barColor = CHART_GREEN,
	title = "Race & Ethnicity",
}) => {
	const raceRows = getRaceRows(raceMetric);
	const ethnicityRows = getEthnicityRows(ethnicityMetric);
	const rows = [...raceRows, ...ethnicityRows];
	const max = Math.max(...rows.map((row) => row.value), 0);

	const renderRows = (items: RaceChartRow[]) => items.map((row) => {
		const width = max > 0 ? Math.max((row.value / max) * 100, 1) : 0;

		return (
			<View key={`${row.section}-${row.name}`} style={raceChartStyles.row}>
				<Text style={raceChartStyles.label}>{row.name}</Text>
				<View style={raceChartStyles.track}>
					<View
						style={[
							raceChartStyles.bar,
							{ width: `${width}%`, backgroundColor: barColor },
						]}
					>
						{row.share !== null && width >= 18 ? (
							<Text style={raceChartStyles.percentage}>
								{`${(row.share * 100).toFixed(0)}%`}
							</Text>
						) : null}
					</View>
				</View>
				<Text style={raceChartStyles.value}>{formatValueBase(row.value, "integer")}</Text>
			</View>
		);
	});

	return (
		<View style={raceChartStyles.card} wrap={false}>
			<Text style={raceChartStyles.title}>{title}</Text>
			{rows.length === 0 ? (
				<Text style={raceChartStyles.empty}>No data available</Text>
			) : (
				<>
					{raceRows.length ? <Text style={raceChartStyles.section}>RACE</Text> : null}
					{renderRows(raceRows)}
					{ethnicityRows.length ? (
						<>
							<View style={raceChartStyles.divider} />
							<Text style={raceChartStyles.section}>ETHNICITY</Text>
							{renderRows(ethnicityRows)}
						</>
					) : null}
				</>
			)}
		</View>
	);
};

interface PDFAgeByGenderChartProps {
	maleMetric?: DisaggregatedMetric;
	femaleMetric?: DisaggregatedMetric;
	maleColor?: string;
	femaleColor?: string;
	title?: string;
	subtitle?: string;
}

const AGE_ORDER = ["<20", "20-29", "30-44", "45-64", "65+"];

const ageChartStyles = StyleSheet.create({
	card: {
		borderWidth: 1,
		borderColor: "#D9E0E5",
		borderRadius: 8,
		backgroundColor: "#FFFFFF",
		padding: 12,
	},
	title: { color: "#243746", fontSize: 11, fontWeight: 700 },
	subtitle: { color: "#65727C", fontSize: 8, marginTop: 2 },
	summary: { flexDirection: "row", justifyContent: "center", marginTop: 8, marginBottom: 9 },
	summaryItem: { flexDirection: "row", alignItems: "center", marginHorizontal: 10 },
	swatch: { width: 8, height: 8, borderRadius: 4, marginRight: 4 },
	summaryText: { color: "#243746", fontSize: 9, fontWeight: 700 },
	chart: {
		height: 155,
		flexDirection: "row",
		alignItems: "flex-end",
		borderBottomWidth: 1,
		borderBottomColor: "#A9B6BC",
		paddingHorizontal: 8,
	},
	group: { width: "20%", alignItems: "center" },
	bars: { height: 130, flexDirection: "row", alignItems: "flex-end" },
	barColumn: {
		width: 38,
		height: 130,
		marginHorizontal: 3,
		justifyContent: "flex-end",
		alignItems: "center",
	},
	barValue: { color: "#4D4D4D", fontSize: 5.5, marginBottom: 2 },
	bar: { width: 18, minHeight: 1, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
	age: { color: "#4D4D4D", fontSize: 7.5, fontWeight: 700, marginTop: 5 },
	empty: { color: "#65727C", fontSize: 9, paddingVertical: 18, textAlign: "center" },
});

const normalizeAgeLabel = (value: string) => {
	const cleaned = value
		.replace(/\u00A0/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

	if (cleaned === "under 20") return "<20";

	return cleaned
		.replace(/^(\d+)\s*-\s*(\d+)$/, "$1-$2")
		.replace(/\b(\d+)\+\b/, "$1+");
};

const sortAgeLabels = (a: string, b: string) => {
	const aIndex = AGE_ORDER.indexOf(a);
	const bIndex = AGE_ORDER.indexOf(b);

	if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
	if (aIndex !== -1) return -1;
	if (bIndex !== -1) return 1;
	return a.localeCompare(b);
};

const buildAgeMap = (metric?: DisaggregatedMetric) => {
	const map = new Map<string, number>();

	for (const item of metric?.versions?.[0]?.disaggregations ?? []) {
		map.set(normalizeAgeLabel(item.name), safeNumber(item.value));
	}

	return map;
};

const PDFAgeByGenderChart: React.FC<PDFAgeByGenderChartProps> = ({
	maleMetric,
	femaleMetric,
	maleColor = CHART_GREEN,
	femaleColor = "#6FAEDE",
	title = "Age & Sex",
	subtitle = "Population distribution by age and sex",
}) => {
	const maleMap = buildAgeMap(maleMetric);
	const femaleMap = buildAgeMap(femaleMetric);
	const labels = Array.from(new Set([...maleMap.keys(), ...femaleMap.keys()]))
		.sort(sortAgeLabels);
	const rows = labels.map((name) => ({
		name,
		male: maleMap.get(name) ?? 0,
		female: femaleMap.get(name) ?? 0,
	}));
	const maleTotal = rows.reduce((sum, row) => sum + row.male, 0);
	const femaleTotal = rows.reduce((sum, row) => sum + row.female, 0);
	const total = maleTotal + femaleTotal;
	const max = Math.max(...rows.flatMap((row) => [row.male, row.female]), 0);
	const unit = maleMetric?.versions?.[0]?.unit
		?? femaleMetric?.versions?.[0]?.unit
		?? "integer";

	return (
		<View style={ageChartStyles.card} wrap={false}>
			<Text style={ageChartStyles.title}>{title}</Text>
			<Text style={ageChartStyles.subtitle}>{subtitle}</Text>

			{rows.length === 0 ? (
				<Text style={ageChartStyles.empty}>No data available</Text>
			) : (
				<>
					<View style={ageChartStyles.summary}>
						<View style={ageChartStyles.summaryItem}>
							<View style={[ageChartStyles.swatch, { backgroundColor: femaleColor }]} />
							<Text style={ageChartStyles.summaryText}>
								{`Female ${formatValue(total ? femaleTotal / total : 0, "percentage", 1360)}`}
							</Text>
						</View>
						<View style={ageChartStyles.summaryItem}>
							<View style={[ageChartStyles.swatch, { backgroundColor: maleColor }]} />
							<Text style={ageChartStyles.summaryText}>
								{`Male ${formatValue(total ? maleTotal / total : 0, "percentage", 1367)}`}
							</Text>
						</View>
					</View>

					<View style={ageChartStyles.chart}>
						{rows.map((row) => {
							const femaleHeight = max > 0 ? Math.max((row.female / max) * 108, 1) : 0;
							const maleHeight = max > 0 ? Math.max((row.male / max) * 108, 1) : 0;

							return (
								<View key={row.name} style={ageChartStyles.group}>
									<View style={ageChartStyles.bars}>
										<View style={ageChartStyles.barColumn}>
											<Text style={ageChartStyles.barValue}>
												{formatValueBase(row.female, unit)}
											</Text>
											<View
												style={[
													ageChartStyles.bar,
													{ height: femaleHeight, backgroundColor: femaleColor },
												]}
											/>
										</View>
										<View style={ageChartStyles.barColumn}>
											<Text style={ageChartStyles.barValue}>
												{formatValueBase(row.male, unit)}
											</Text>
											<View
												style={[
													ageChartStyles.bar,
													{ height: maleHeight, backgroundColor: maleColor },
												]}
											/>
										</View>
									</View>
									<Text style={ageChartStyles.age}>{row.name}</Text>
								</View>
							);
						})}
					</View>
				</>
			)}
		</View>
	);
};

const uniqueById = (metrics: DisaggregatedMetric[]) => {
	const seen = new Set<number>();
	return metrics.filter((metric) => {
		if (seen.has(metric.id)) return false;
		seen.add(metric.id);
		return true;
	});
};

const numericIndexValue = (metric: DisaggregatedMetric) => {
	const version = metric.versions?.[0];
	if (!version) return 0;

	if (version.unit === "ratio" && version.percent_calc) {
		const numerator = safeNumber(version.percent_calc.n);
		const denominator = safeNumber(version.percent_calc.d);
		return denominator !== 0 ? numerator / denominator : 0;
	}

	return safeNumber(version.value);
};

const buildIndexMetricMap = (
	metadata: MetricMetadata[] | null,
	disaggregatedData: DisaggregatedMetric[],
) => PDF_CATEGORIES.reduce<IndexMetricMap>((result, category) => {
	result[category] = getIndexMetricDataPDF(category, metadata, disaggregatedData) ?? null;
	return result;
}, {
	Demographics: null,
	Accessibility: null,
	Livability: null,
	Education: null,
	Economy: null,
	Environment: null,
});

/**
 * Resolve every index label before creating or displaying the PDF.
 * This must run in the normal application React tree, not inside the
 * @react-pdf/renderer document tree.
 */
export const prepareMetricsPDFIndexData = async (
	metadata: MetricMetadata[] | null,
	disaggregatedData: DisaggregatedMetric[],
): Promise<PreparedMetricsPDFIndexData> => {
	const indexMetrics = buildIndexMetricMap(metadata, disaggregatedData);
	const missingCategories = PDF_CATEGORIES.filter((category) => !indexMetrics[category]);

	if (missingCategories.length > 0) {
		throw new Error(
			`Unable to prepare the PDF because index metrics are missing for: ${missingCategories.join(", ")}.`,
		);
	}

	const metrics = PDF_CATEGORIES
		.map((category) => indexMetrics[category])
		.filter((metric): metric is DisaggregatedMetric => Boolean(metric));

	const entries = await Promise.all(metrics.map(async (metric) => {
		const response = await fetchPercentileById(metric.id);

		if (!response?.data?.length) {
			throw new Error(`No percentile data was returned for index metric ${metric.id}.`);
		}

		const category = categorizeIndexValue(
			numericIndexValue(metric),
			response.data,
			false,
		);

		return [metric.id, category] as const;
	}));

	return {
		indexMetrics,
		indexCategories: Object.fromEntries(entries),
	};
};

const DocumentChrome: React.FC<{ selectedArea: string }> = ({ selectedArea }) => (
	<>
		<View style={styles.documentHeader} fixed>
			<Text style={styles.documentHeaderText}>City of Lacey Community Indicators</Text>
			<Text style={styles.documentHeaderArea}>{selectedArea || "City of Lacey"}</Text>
		</View>
		<View style={styles.divider} fixed />
	</>
);

const PageFooter: React.FC = () => (
	<View style={styles.footer} fixed>
		<Text style={styles.footerText}>City of Lacey Community Indicators</Text>
		<Text
			style={styles.footerText}
			render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
		/>
	</View>
);

const TitleBanner: React.FC<{
	title: LaceyMetricCategory | "Equity";
	indexMetric: DisaggregatedMetric | null;
	indexCategory?: string;
}> = ({ title, indexMetric, indexCategory = "–" }) => (
	<View style={styles.titleBanner} wrap={false}>
		<View style={styles.titleBannerHeading}>
			<Image src={TITLE_BANNER_ICONS[title]} style={styles.titleBannerIcon} />
			<Text style={styles.titleBannerTitle}>{title}</Text>
		</View>
		<Text style={styles.titleBannerIndex}>
			{`The ${indexMetric?.name ?? "Selected Index"} is `}
			<Text style={styles.titleBannerIndexStrong}>{indexCategory}</Text>
		</Text>
	</View>
);

const MetricCard: React.FC<{ metric: DisaggregatedMetric }> = ({ metric }) => {
	const version = metric.versions?.[0];
	if (!version) return null;

	const isIndexMetric = indexMetricIds.includes(metric.id);
	const icon = getPDFMetricIcon(metric.id);
	const displayValue = version.unit === "ratio"
		? `${version.percent_calc?.n ?? 0}/${version.percent_calc?.d ?? 0}`
		: version.value;

	return (
		<View
			style={isIndexMetric ? [styles.metricCard, styles.metricCardIndex] : styles.metricCard}
			wrap={false}
		>
			<View style={styles.metricCardContent}>
				<View style={styles.metricTopRow}>
					{icon ? <Image src={icon} style={styles.metricIcon} /> : null}
					<Text style={styles.metricValue}>
						{formatValue(displayValue, version.unit, metric.id)}
					</Text>
				</View>
				<Text style={styles.metricTitle}>{metric.name}</Text>
				{version.unit_descriptor ? (
					<Text style={styles.metricDescriptor}>{version.unit_descriptor}</Text>
				) : null}
				{isIndexMetric ? <Text style={styles.indexBadge}>Index</Text> : null}
			</View>
		</View>
	);
};

const CategoryPage: React.FC<{
	title: LaceyMetricCategory | "Equity";
	selectedArea: string;
	metrics: DisaggregatedMetric[];
	indexMetric: DisaggregatedMetric | null;
	indexCategory?: string;
	description?: string;
	additionalContent?: React.ReactNode;
}> = ({ title, selectedArea, metrics, indexMetric, indexCategory, description, additionalContent }) => (
	<Page size="LETTER" style={styles.page} wrap>
		<DocumentChrome selectedArea={selectedArea} />
		<TitleBanner title={title} indexMetric={indexMetric} indexCategory={indexCategory} />
		{description ? <Text style={styles.description}>{description}</Text> : null}
		{metrics.length ? (
			<View style={styles.metricGrid}>
				{metrics.map((metric) => <MetricCard key={metric.id} metric={metric} />)}
			</View>
		) : (
			<Text style={styles.empty}>No metrics available for this category.</Text>
		)}
		{additionalContent}
		<PageFooter />
	</Page>
);

const MetricsPDF: React.FC<MetricsPDFProps> = ({
	pdfImageBlob,
	pdfUrl,
	logoUrl,
	legendUrl,
	selectedArea,
	siteUrl,
	disaggregatedData,
	indexMetrics,
	indexCategories,
}) => {
	const categorizedData = categorizeData(disaggregatedData);

	const getCategoryMetrics = (category: LaceyMetricCategory) => {
		const indexId = indexMetrics[category]?.id;
		return uniqueById(getSortedMetrics(category, categorizedData).flat())
			.filter((metric) => metric.id !== indexId);
	};

	const demographicSorted = getCategoryMetrics("Demographics");
	const hasCityPopulation = demographicSorted.some((metric) => metric.id === 1184);
	const demographicMetrics = demographicSorted.filter((metric) => {
		if (SPECIAL_DEMOGRAPHIC_IDS.has(metric.id)) return false;
		if (hasCityPopulation && metric.id === 1133) return false;
		return true;
	});

	const race = disaggregatedData.find((metric) => metric.id === 1134);
	const ethnicity = disaggregatedData.find((metric) => metric.id === 1135);
	const immigration = disaggregatedData.find((metric) => metric.id === 1182);
	const female = disaggregatedData.find((metric) => metric.id === 1147);
	const male = disaggregatedData.find((metric) => metric.id === 1148);
	const equityIndex = indexMetrics.Demographics;

	const indexLabel = (category: LaceyMetricCategory) => {
		const id = indexMetrics[category]?.id;
		return id ? indexCategories[id] ?? "–" : "–";
	};

	const mapImage = pdfImageBlob ?? pdfUrl;

	return (
		<Document title={`City of Lacey Community Indicators – ${selectedArea || "City of Lacey"}`}>
			<Page size="LETTER" style={styles.page} wrap={false}>
				<View style={styles.mapHeader}>
					<View style={styles.laceyLogoContainer}>
						<Image
							src={laceyImg}
							style={styles.laceyLogo}
						/>
					</View>

					<View style={styles.mapHeaderCopy}>
						<Text style={styles.mapHeaderTitle}>City of Lacey, WA</Text>
						<Text style={styles.mapHeaderText}>
							{selectedArea || "Lacey, Washington"}
						</Text>

						{siteUrl ? (
							<Text style={styles.mapHeaderUrl}>{siteUrl}</Text>
						) : null}
					</View>
				</View>
				{mapImage ? (
					<Image src={mapImage} style={styles.mapImage} />
					) : null}

					{legendUrl ? (
					<View style={styles.legendOverlay} wrap={false}>
						<Image src={legendUrl} style={styles.legendImage} />
					</View>
					) : null}

					{logoUrl ? (
					<View style={styles.mapAssets}>
						<Image src={logoUrl} style={styles.esriLogo} />
					</View>
				) : null}
				<PageFooter />
			</Page>

			<CategoryPage
				title="Equity"
				selectedArea={selectedArea}
				metrics={demographicMetrics}
				indexMetric={equityIndex}
				indexCategory={indexLabel("Demographics")}
				description="Indicators shown below depict demographic details for the population within the selected area."
				additionalContent={(race || ethnicity) ? (
					<View style={styles.chartStack}>
						<PDFRaceEthnicityChart
							raceMetric={race}
							ethnicityMetric={ethnicity}
							barColor={CHART_GREEN}
						/>
					</View>
				) : null}
			/>

			{(immigration || female || male) ? (
				<Page size="LETTER" style={styles.page} wrap>
					<DocumentChrome selectedArea={selectedArea} />
					<TitleBanner
						title="Equity"
						indexMetric={equityIndex}
						indexCategory={indexLabel("Demographics")}
					/>
					<View style={styles.chartStack}>
						{immigration ? (
							<View style={styles.chartSpacing} wrap={false}>
								<PDFMetricBarChart
									metric={immigration}
									title="Top Countries of Immigration"
									topFive
									barColor={CHART_GREEN}
								/>
							</View>
						) : null}
						{(female || male) ? (
							<PDFAgeByGenderChart
								maleMetric={male}
								femaleMetric={female}
								maleColor={CHART_GREEN}
								femaleColor="#6FAEDE"
							/>
						) : null}
					</View>
					<PageFooter />
				</Page>
			) : null}

			{(["Accessibility", "Livability", "Education", "Economy", "Environment"] as LaceyMetricCategory[])
				.map((category) => (
					<CategoryPage
						key={category}
						title={category}
						selectedArea={selectedArea}
						metrics={getCategoryMetrics(category)}
						indexMetric={indexMetrics[category]}
						indexCategory={indexLabel(category)}
					/>
				))}
		</Document>
	);
};

/**
 * Preferred entry point for PDF generation. Awaiting this function guarantees
 * that every index classification is resolved before a Document is returned.
 */
export const createMetricsPDFDocument = async ({
	metadata,
	...pdfProps
}: MetricsPDFSourceProps) => {
	const preparedIndexData = await prepareMetricsPDFIndexData(
		metadata,
		pdfProps.disaggregatedData,
	);

	return <MetricsPDF {...pdfProps} {...preparedIndexData} />;
};

export default MetricsPDF;